import { IconContainerProps, Rating, Stack, styled } from "@mui/material";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { FornecedorAvaliacao } from "../../../../domain/models/fornecedor_avaliacao";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useMemo } from "react";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { FornecedorAvaliacaoItem } from "../../../../domain/models/fornecedor_avaliacao_item";
import { FornecedorAvaliacaoEditarForm, FornecedorAvaliacaoEditarSchema } from "../../../schemas/Fornecedor_cadastro/fornecedor_avaliacao_editar";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import dayjs from "dayjs";

/**
 * Icons
 */

const customIcons: {
  [index: string]: {
    textColor: string;
    icon: React.ReactElement<unknown>;
    label: string;
  };
} = {
  1: {
    textColor: 'text-red-500',
    icon: <SentimentVeryDissatisfiedIcon className="text-red-500" />,
    label: 'Very Dissatisfied',
  },
  2: {
    textColor: 'text-yellow-500',
    icon: <SentimentSatisfiedIcon className="text-yellow-500" />,
    label: 'Neutral',
  },
  3: {
    textColor: 'text-green-600',
    icon: <SentimentVerySatisfiedIcon className="text-green-600" />,
    label: 'Very Satisfied',
  },
};

function IconContainer(props: IconContainerProps) {
  const { value, ...other } = props;
  const isSelected = other.className?.includes('MuiRating-iconFilled');
  return (
    <span {...other}>
      <div className="flex flex-col items-center">
        <span className="flex">
          {customIcons[value].icon}
        </span>
        <span className={`${isSelected ? customIcons[value].textColor : ''} text-xs`}>
          {typeof value === 'number' && value}
        </span>
      </div >
    </span >
  );
}

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
    color: theme.palette.action.disabled,
  },
}));

/**
 * FornecedorAvaliacaoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedAvaliacao: FornecedorAvaliacao | null;
  fornecedorAvaliacaoItems: FornecedorAvaliacaoItem[];
  funcionariosDropdown: FuncionarioDropDown[];
  onClose: () => void;
  onSubmit: (form: FornecedorAvaliacaoEditarForm) => void;
};

export const FornecedorAvaliacaoModal = ({
  open,
  saving,
  loading,
  selectedAvaliacao,
  fornecedorAvaliacaoItems,
  funcionariosDropdown,
  onClose,
  onSubmit,
}: Props) => {
  const hookForm = useForm<FornecedorAvaliacaoEditarForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(FornecedorAvaliacaoEditarSchema),
  });

  const [items] = hookForm.watch(['items']);

  useEffect(() => {
    if (open) {
      if (selectedAvaliacao) {
        hookForm.reset();

        if (selectedAvaliacao.idFuncionarioAvaliador !== null) {
          hookForm.setValue('avaliadoPor', selectedAvaliacao.idFuncionarioAvaliador);
        }
        if (selectedAvaliacao.data !== null) {
          hookForm.setValue('data', dayjs(selectedAvaliacao.data).toDate())
        }
        if (fornecedorAvaliacaoItems && fornecedorAvaliacaoItems.length > 0) {
          hookForm.setValue('items', fornecedorAvaliacaoItems.map((x): any => ({
            idItemAvaliacaoFornecedor: x.idItemAvaliacaoFornecedor,
            descricao: x.descricao,
            nota: x.nota,
          })));
        }
        hookForm.setValue('observacao', selectedAvaliacao.observacao ?? '');
      }
    }
  }, [
    open,
    selectedAvaliacao,
    fornecedorAvaliacaoItems,
    hookForm,
  ]);

  const columns = useMemo((): GridColDef[] => {
    return [
      {
        field: "descricao",
        filterable: false,
        headerName: "Item",
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: ({ value }) => {
          return (
            <div className="py-2">{value}</div>
          );
        }
      },
      {
        field: "nota",
        filterable: false,
        headerName: "Selecione a Nota",
        width: 150,
        sortable: false,
        align: 'center',
        valueGetter: (_, rows) => {
          return [rows['idItemAvaliacaoFornecedor'] as number, rows['nota'] as number];
        },
        renderCell: ({ value: [idItemAvaliacaoFornecedor, nota] }) => {
          return (
            <div className="flex flex-col justify-center items-center py-2">
              <StyledRating
                className="w-full h-full"
                getLabelText={(value: number) => customIcons[value].label}
                highlightSelectedOnly={true}
                value={nota}
                max={3}
                onChange={(_, newNota) => {
                  const notas = hookForm.getValues('items');
                  const notaIndex = notas.findIndex(x => x.idItemAvaliacaoFornecedor === idItemAvaliacaoFornecedor);

                  if (notaIndex >= 0) {
                    notas[notaIndex].nota = newNota ?? 0;
                    hookForm.setValue('items', [...notas]);
                  }
                }}
                slotProps={{
                  root: {
                    className: "flex flex-row justify-center items-center gap-2",
                  },
                  icon: {
                    component: IconContainer,
                  }
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      width='60rem'
      title="Avaliação de fornecedor"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title="Informações" arrangement="fullRow" border={false} paddingTop="normal" paddingBottom="small">
        <div className='w-full grid grid-cols-1 md:grid-cols-2 gap-4'>
          <DefaultFormAutocomplete
            name='avaliadoPor'
            label='Avaliado Por'
            required={true}
            getOptionDisabled={x => !x.canBeSelected}
            options={funcionariosDropdown}
            getOptionKey={x => x.idFuncionario}
            getOptionLabel={x => x.nome}
            reactHookForm={hookForm}
          />

          <FormDatePickerInput
            name='data'
            label='Data'
            required={true}
            reactHookForm={hookForm}
          />
        </div>
      </FormContainer>

      <FormContainer title="Critérios de Avaliação" arrangement="fullRow" paddingTop="small" paddingBottom="small">
        <>
          <Stack>
            <StyledDataGrid
              tableName="fornecedor_avaliacao_fornecedor_modal"
              height='22.5rem'
              getRowHeight={() => 'auto'}
              disableColumnMenu={false}
              disableRowSelectionOnClick={true}
              disableColumnSorting={true}
              disableColumnResize={true}
              getRowId={(row: any) => row.idItemAvaliacaoFornecedor}
              columns={columns}
              loading={saving}
              rows={items ?? []}
              showToolbar={false}
            />

            <div className="w-full flex flex-row justify-between">
              <div>
                {hookForm.formState.errors.root &&
                  <span className="text-red-500 text-sm">
                    {hookForm.formState.errors.root.message}
                  </span>
                }
              </div>
            </div>
          </Stack>
        </>
      </FormContainer>
      <FormContainer title="Observação" arrangement="fullRow" paddingTop="small" paddingBottom="normal">
        <div className="mt-2 flex flex-col">
          <DefaultFormInput
            name='observacao'
            label='Observações'
            multiline={true}
            rows={5}
            reactHookForm={hookForm}
          />
        </div>
      </FormContainer>
    </BaseFormControlledModal>
  );
}